import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { RESOURCE_TYPE_COMPONENTS } from '../resourceTypeComponents'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Pagination from '@mui/material/Pagination'
import Loading from '../loading/Loading'
import IconButton from '@mui/material/IconButton'
import ReorderIcon from '@mui/icons-material/Reorder'
import AppsIcon from '@mui/icons-material/Apps'

import { setErrorNotification } from '../../reducers/notificationReducer'
import quizService from '../../services/quiz'
import { PAGES } from '../navigation/routes'
import { PAGE_SIZE } from '../websiteConfig'
import { SearchStyle, RecommendedStyle } from '../styles'

export const PENDING_QUIZ_KEY = 'TCAIREMPendingQuiz'

const hasSelectedTags = (results) => (
    Boolean(results) && (
        (results.experience_tags && results.experience_tags.length > 0) ||
        (results.interest_categories && results.interest_categories.length > 0) ||
        (results.demographic_tags && results.demographic_tags.length > 0)
    )
)

const getPendingQuizResults = () => {
    try {
        const pending = sessionStorage.getItem(PENDING_QUIZ_KEY)
        if (!pending) return null

        const parsed = JSON.parse(pending)
        return {
            experience_tags: Array.isArray(parsed.experience_tags) ? parsed.experience_tags : [],
            interest_categories: Array.isArray(parsed.interest_categories) ? parsed.interest_categories : [],
            demographic_tags: Array.isArray(parsed.demographic_tags) ? parsed.demographic_tags : [],
        }
    } catch {
        return null
    }
}

const Recommended = () => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [groups, setGroups] = useState(null)
    const [loading, setLoading] = useState(true)
    const [list, setList] = useState(true)
    const [page, setPage] = useState(1)
    const pageSize = PAGE_SIZE
    const isUserSessionLoading = user === null
    const pendingQuizResults = useMemo(() => (
        user?.anon ? getPendingQuizResults() : null
    ), [user])

    useEffect(() => {
        if (isUserSessionLoading) {
            setLoading(false)
            return
        }

        setLoading(true)

        if (user.anon && !hasSelectedTags(pendingQuizResults)) {
            setGroups([])
            setLoading(false)
            return
        }

        const fetchRecommended = async () => {
            try {
                const data = await quizService.getRecommended(user.anon ? pendingQuizResults : null)
                setGroups(data)
            } catch {
                dispatch(setErrorNotification('Error loading recommendations'))
                setGroups([])
            } finally {
                setLoading(false)
            }
        }
        fetchRecommended()
    }, [user, pendingQuizResults, dispatch, isUserSessionLoading])

    const hasQuizResults = user && (
        user.anon
            ? hasSelectedTags(pendingQuizResults)
            : (
                (user.quiz_experience_tags && user.quiz_experience_tags.length > 0) ||
                (user.quiz_interest_categories && user.quiz_interest_categories.length > 0) ||
                (user.quiz_demographic_tags && user.quiz_demographic_tags.length > 0)
            )
    )

    const totalResources = groups ? groups.reduce((sum, g) => sum + g.resources.length, 0) : 0
    const totalPages = Math.ceil(totalResources / pageSize)
    const pagedGroups = useMemo(() => {
        if (!groups || groups.length === 0) return []

        let offsetToSkip = (page - 1) * pageSize
        let itemsToTake = pageSize
        const currentPageGroups = []

        for (const group of groups) {
            if (itemsToTake <= 0) break

            const groupCount = group.resources.length
            if (offsetToSkip >= groupCount) {
                // Skip entire groups until we reach the current page window.
                offsetToSkip -= groupCount
                continue
            }

            const sliceStart = offsetToSkip
            const sliceEnd = Math.min(groupCount, sliceStart + itemsToTake)
            const pageResources = group.resources.slice(sliceStart, sliceEnd)
            if (pageResources.length > 0) {
                currentPageGroups.push({
                    ...group,
                    resources: pageResources,
                })
            }

            // After the first partial slice, continue taking from subsequent groups.
            itemsToTake -= (sliceEnd - sliceStart)
            offsetToSkip = 0
        }

        return currentPageGroups
    }, [groups, page, pageSize])

    useEffect(() => {
        setPage(1)
    }, [groups])

    const renderCard = (resource) => {
        const type = resource.type
        const Component = RESOURCE_TYPE_COMPONENTS[type]?.[list ? 'List' : 'Card']
        if (Component) return <Component key={`${type}-${resource.id}`} {...resource} />
        console.warn(`Unrecognized resource type in renderCard: ${type} (id=${resource.id})`)
        return null
    }

    const renderGroup = (group) => (
        <Box key={group.category?.id ?? 'uncategorized'} sx={RecommendedStyle.categorySection}>
            {group.category && (
                <>
                    <Typography variant="h5" sx={RecommendedStyle.categorySectionHeader}>
                        {group.category.name}
                    </Typography>
                    <Divider sx={{ marginBottom: '16px' }} />
                </>
            )}
            <Grid container spacing={2}>
                {group.resources.map(resource => (
                    <Grid
                        key={`${resource.type}-${resource.id}`}
                        item
                        xs={12}
                        md={list ? 12 : 4}
                        sx={list ? {} : SearchStyle.card}
                    >
                        {renderCard(resource)}
                    </Grid>
                ))}
            </Grid>
        </Box>
    )

    return (
        <Container sx={SearchStyle.container}>
            <Typography {...SearchStyle.title} gutterBottom>
                Recommended Resources
            </Typography>
            <Typography variant="body1" gutterBottom sx={RecommendedStyle.subtitle}>
                Resources tailored to your experience level, interests, and optional demographics based on your quiz results.
            </Typography>
            <Box sx={RecommendedStyle.quizButtonContainer}>
                <Button variant="outlined" onClick={() => navigate(PAGES.quiz.path)}>
                    {hasQuizResults ? 'Retake the Quiz' : 'Take the Quiz'}
                </Button>
            </Box>

            {isUserSessionLoading && (
                <Typography variant="body1" color="text.secondary" sx={RecommendedStyle.emptySubtitle}>
                    Loading your user session...
                </Typography>
            )}

            {loading && !isUserSessionLoading && <Loading />}

            {!loading && !isUserSessionLoading && !hasQuizResults && (
                <Box sx={RecommendedStyle.emptyContainer}>
                    <Typography variant="h6" gutterBottom color="text.secondary">
                        You haven't taken the quiz yet.
                    </Typography>
                    <Typography variant="body1" gutterBottom color="text.secondary" sx={RecommendedStyle.emptySubtitle}>
                        Take the quiz to get personalized resource recommendations.
                    </Typography>
                    <Button variant="contained" onClick={() => navigate(PAGES.quiz.path)}>
                        Take the Quiz
                    </Button>
                </Box>
            )}

            {!loading && !isUserSessionLoading && hasQuizResults && groups !== null && (
                <>
                    <Box sx={SearchStyle.viewContainer}>
                        <IconButton sx={list ? RecommendedStyle.viewIconActive : RecommendedStyle.viewIconInactive} onClick={() => setList(true)}>
                            <ReorderIcon />
                        </IconButton>
                        <IconButton sx={!list ? RecommendedStyle.viewIconActive : RecommendedStyle.viewIconInactive} onClick={() => setList(false)}>
                            <AppsIcon />
                        </IconButton>
                    </Box>
                    {totalResources > 0 ? (
                        <>
                            {pagedGroups.map(renderGroup)}
                            <Pagination
                                sx={SearchStyle.pagination}
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                            />
                        </>
                    ) : (
                        <Typography {...SearchStyle.noResources.props} gutterBottom sx={SearchStyle.noResources.style}>
                            No recommended resources found. Try updating your quiz answers or check back later as new resources are added.
                        </Typography>
                    )}
                </>
            )}

            <Box sx={RecommendedStyle.disclaimer}>
                <Typography variant="overline" component="p">
                    All resources provided are strictly owned and distributed by the creators indicated on the resource
                </Typography>
            </Box>
        </Container>
    )
}

export default Recommended
